/**
 * Extracts files from a drag-and-drop event with sensible fallbacks for
 * email clients and webmail attachments that don't expose real files via
 * the standard `event.dataTransfer.files` array.
 *
 * Background:
 *   - Finder / Windows Explorer / Apple Mail / Thunderbird drops populate
 *     `dataTransfer.files` directly. Standard path.
 *   - Some browsers/clients only populate `dataTransfer.items` instead, so
 *     we fall back to walking those and calling `getAsFile()`.
 *   - Outlook desktop on Windows (older Chromium / Firefox) places the
 *     attachment into proprietary clipboard formats (FileGroupDescriptorW
 *     + FileContents) that the HTML5 drag-drop API cannot read. Both
 *     `files` and `items` come up empty even though `types` includes
 *     "Files". Nothing JS can do to recover the bytes.
 *   - Webmail clients (Gmail, Outlook Web App, Yahoo) place a URL/HTML
 *     link into the drag payload (`text/uri-list`, `text/html`) instead
 *     of a file. Again, nothing we can do — user must download first.
 *
 * This helper extracts what it can and otherwise returns a user-friendly
 * error string explaining the most likely cause and the workaround.
 */

const URI_TYPES = ['text/uri-list', 'text/x-moz-url'];

const FILE_NOT_READABLE_MESSAGE =
    'Your email app placed the attachment in a format the browser cannot read. ' +
    'Please save the attachment to your computer first, then drag it here or click "browse files".';

const URL_DROPPED_MESSAGE =
    'It looks like you dragged a link instead of a file. Webmail attachments ' +
    '(Gmail, Outlook Web, etc.) cannot be dragged directly into the browser. ' +
    'Please download the attachment first, then drag the saved file here.';

const TEXT_DROPPED_MESSAGE =
    'The drop contained text instead of a file. Please save the email attachment ' +
    'to your computer first, then drag the saved file here.';

const NO_FILE_MESSAGE =
    'No file was detected in the drop. Some email clients do not support ' +
    'drag-and-drop into a browser. Please save the attachment to your computer ' +
    'first, then drag it here or click "browse files".';

/**
 * Extracts all files from a drop event.
 * @param {DragEvent} e
 * @returns {{ files: File[], error: string|null }}
 */
export const extractFilesFromDropEvent = (e) => {
    if (!e || !e.dataTransfer) {
        return { files: [], error: NO_FILE_MESSAGE };
    }

    const dt = e.dataTransfer;
    const types = Array.from(dt.types || []);

    if (dt.files && dt.files.length > 0) {
        return { files: Array.from(dt.files), error: null };
    }

    if (dt.items && dt.items.length > 0) {
        const itemFiles = [];
        for (const item of dt.items) {
            if (item.kind === 'file') {
                const f = typeof item.getAsFile === 'function' ? item.getAsFile() : null;
                if (f) itemFiles.push(f);
            }
        }
        if (itemFiles.length > 0) {
            return { files: itemFiles, error: null };
        }
    }

    const hasFilesType = types.includes('Files');
    const hasUri = types.some((t) => URI_TYPES.includes(t));
    const hasHtml = types.includes('text/html');
    const hasText = types.includes('text/plain');

    let error;
    if (hasFilesType) {
        error = FILE_NOT_READABLE_MESSAGE;
    } else if (hasUri || hasHtml) {
        error = URL_DROPPED_MESSAGE;
    } else if (hasText) {
        error = TEXT_DROPPED_MESSAGE;
    } else {
        error = NO_FILE_MESSAGE;
    }

    return { files: [], error };
};

/**
 * Convenience wrapper for single-file drop zones.
 * @param {DragEvent} e
 * @returns {{ file: File|null, error: string|null }}
 */
export const extractFileFromDropEvent = (e) => {
    const { files, error } = extractFilesFromDropEvent(e);
    return { file: files[0] || null, error };
};
